import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { todayIso } from '../../dates'
import { addDays } from '../../record/format'
import type { TaskProposal } from '../types'
import { SheetFrame } from './SheetFrame'

/** Edit a proposed task: title, owner, deadline (quick picks or a date); the relation follows "Wie". */
export function TaskSheet({
  task,
  relationName,
  onSave,
  onClose,
}: {
  task: TaskProposal
  relationName: string | null
  onSave: (values: Record<string, unknown>) => void
  onClose: () => void
}) {
  const { membersById } = useTeamMembers()
  const [title, setTitle] = useState(task.title)
  const [owner, setOwner] = useState(task.owner_user_id ?? '')
  const [due, setDue] = useState(task.due_on ?? '')
  const today = todayIso()
  const picks = [
    { label: 'Morgen', day: addDays(today, 1) },
    { label: 'Volgende week', day: addDays(today, 7) },
    { label: 'Over een maand', day: addDays(today, 30) },
  ]
  return (
    <SheetFrame
      title="Taak bewerken"
      onClose={onClose}
      saveDisabled={!title.trim() || !owner}
      onSave={() => onSave({ title: title.trim(), owner_user_id: owner, due_on: due || null })}
    >
      <FormField label="Titel" required>
        <Input value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} />
      </FormField>
      <FormField label="Eigenaar" required>
        <NativeSelect value={owner} onChange={(event) => setOwner(event.target.value)}>
          <option value="">Kies een eigenaar</option>
          {[...(membersById?.values() ?? [])].map((member) => (
            <option key={member.id} value={member.id}>
              {member.display_name}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-semibold">Deadline</span>
        <div className="flex flex-wrap gap-2">
          {picks.map((pick) => (
            <Button
              key={pick.label}
              type="button"
              variant="outline"
              size="sm"
              className="max-md:min-h-11"
              aria-pressed={due === pick.day}
              onClick={() => setDue(pick.day)}
            >
              {pick.label}
            </Button>
          ))}
        </div>
        <Input type="date" aria-label="Deadline" value={due} onChange={(event) => setDue(event.target.value)} />
      </div>
      {relationName ? (
        <FormField label="Relatie" hint="Verander dit onder Wie.">
          <p className="text-base">{relationName}</p>
        </FormField>
      ) : null}
      {task.description ? (
        <FormField label="Toelichting">
          <p className="text-base text-muted-foreground">{task.description}</p>
        </FormField>
      ) : null}
    </SheetFrame>
  )
}
